import { z } from 'zod';

import { PASSWORD_ERROR_CODES } from '@/shared/api/sessions';

/** What the form checks before the API does (the API remains the authority, ISO-28b). */
export const loginSchema = z.object({
  email: z.string().trim().min(1, 'validation.emailRequired').email('validation.emailInvalid'),
  password: z.string().min(1, 'validation.passwordRequired'),
  rememberMe: z.boolean(),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

export const forgotSchema = z.object({
  email: z.string().trim().min(1, 'validation.emailRequired').email('validation.emailInvalid'),
});

export type ForgotFormValues = z.infer<typeof forgotSchema>;

/** 08 §2.7: twelve characters minimum; the API also rejects common passwords. */
export const passwordSchema = z
  .object({
    password: z.string().min(12, 'validation.passwordTooShort'),
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, {
    message: 'validation.passwordMismatch',
    path: ['confirm'],
  });

export type PasswordFormValues = z.infer<typeof passwordSchema>;

/** `/account/security`: same floor as {@link passwordSchema}, plus the current password the API asks to confirm the change. */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'validation.passwordRequired'),
    newPassword: z.string().min(12, 'validation.passwordTooShort'),
    confirmNewPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmNewPassword, {
    message: 'validation.passwordMismatch',
    path: ['confirmNewPassword'],
  });

export type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

export const invitationSchema = z.object({
  password: z.string().min(1, 'validation.passwordRequired'),
});

export type InvitationFormValues = z.infer<typeof invitationSchema>;

/**
 * The message of the `PasswordPolicy` codes a `422` carries on its password field
 * (`errors.newPassword` of the reset, `errors.password` of the invitation): the real reason, never
 * the expired-link notice. `undefined` when none of them is there, so the caller says it failed.
 */
export function passwordPolicyMessage(codes: readonly string[] | undefined): string | undefined {
  if (codes?.includes(PASSWORD_ERROR_CODES.tooShort) === true) {
    return 'validation.passwordTooShort';
  }
  if (codes?.includes(PASSWORD_ERROR_CODES.tooCommon) === true) {
    return 'validation.passwordTooCommon';
  }
  return undefined;
}
